import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { Button, Dialog, Stack, Box, createTheme, ThemeProvider, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import { type Group, type Status } from "@entities";
import { GroupVolunteerRegionsField } from "@shared/frontend/components/GroupVolunteerRegionsField";
import {
  GROUP_VOLUNTEER_REGIONS,
  parseGroupVolunteerRegions,
  type GroupVolunteerRegion,
} from "@shared/helpers/group-volunteer-regions";
import { linko } from "@shared/linko";
import { useAsyncEffect } from "@shared/frontend/hooks";

import { useAlert } from "../../../contexts/AlertProvider";
import { getRpcErrorMessage } from "@shared/frontend/functions/get-rpc-error-message.function";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { getStatusOptions } from "../../../generic-functions/get-status-options.function";
import { yupIdValidation } from "../../../generic-functions/id-number-validation";
import FormProvider, { useForm } from "../../../helpers/forms/FormProvider";
import { provide } from "../../../helpers/wrap-and-provide";
import { FormSelect } from "../../form-components/form-select-input/FormSelect";
import { FormTextField } from "../../form-components/form-text-field/FormTextField";
import { BasicButton } from "../../generic-components/button/Button";
import { SelectButton } from "../../select-buttons/SelectButton";
import { SelectButtonsContainer } from "../../select-buttons/SelectButtonsContainer";
import MissionList from "../../../pages/missions/components/MissionList";
import { GeneralAlert } from "../../alert/GeneralAlert";
import {
  CardInfoChips,
  CardInfoField,
  CardInfoGrid,
  CardInfoHeader,
  CardInfoSection,
  CardInfoView,
} from "../card-info-view/CardInfoView";

import "./GroupCard.scss";
import { CommunicationRecordManager } from "../../CommunicationRecordManager/CommunicationRecordManager";
import { TeamsSelectDialog } from "../../teams-select";
import PhoneNumberCell from "../../../pages/tables/PhoneNumberCell";

interface GroupDrawerProps {
  isOpen: boolean;
  onClose: VoidFunction;
  isEdit: boolean;
  groupDetails?: Group;
  onEditComplete?: VoidFunction;
  onSaveComplete?: VoidFunction;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#05204CF2",
    },
  },
});

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`group-tabpanel-${index}`}
      aria-labelledby={`group-tab-${index}`}
      {...other}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {value === index && children}
    </div>
  );
}

const validationSchema = yup.object().shape({
  id: yupIdValidation.required("שדה זה הינו חובה"),
  name: yup
    .string()
    .min(2, "שם מלא חייב להכיל לפחות 2 תווים")
    .required("שדה זה הינו חובה"),
  phoneNumber: yup
    .string()
    .matches(/^0\d{9}$/, "מספר טלפון חייב להתחיל ב-0 ולהכיל 10 ספרות")
    .length(10, "מספר תעודת זהות חייב להכיל בדיוק 10 ספרות")
    .required("שדה זה הינו חובה"),
  groupName: yup
    .string()
    .min(2, "שם הקבוצה חייב להכיל לפחות 2 תווים")
    .required("שדה זה הינו חובה"),
  description: yup
    .string()
    .transform((value) => (value?.trim() ? value.trim() : undefined))
    .optional()
    .min(5, "תיאור הקבוצה חייב להכיל לפחות 5 תווים"),
  size: yup
    .number()
    .min(1, "גודל הקבוצה חייב להיות גדול מ-0")
    .required("שדה זה הינו חובה"),
  email: yup.string().email("כתובת מייל לא תקינה"),
  comments: yup.string(),
  groupCoordinator: yup.string(),
  transportMode: yup.string(),
  team: yup.object().optional(),
  preferredVolunteerRegions: yup
    .array()
    .of(yup.string().oneOf([...GROUP_VOLUNTEER_REGIONS]))
    .min(1, "יש לבחור לפחות אזור התנדבות אחד")
    .required(),
});

export const GroupCard: FC<GroupDrawerProps> = ({
  isOpen,
  onClose,
  isEdit,
  groupDetails,
  onEditComplete,
  onSaveComplete,
}) => {
  const { setAlertText, setSeverity, setTime } = useAlert();
  const {
    resetForm,
    values,
    isValid,
    setValues,
    setFieldValue,
    errors,
    validateForm,
    setTouched,
  } = useForm<any>();
  const [isLoading, setIsLoading] = useState(false);
  const { currOrganization } = useOrganization();
  const [statusOptions, setStatusOptions] = useState<Status[]>([]);
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [isTeamsSelectOpen, setIsTeamsSelectOpen] = useState<boolean>(false);
  const [detailsViewMode, setDetailsViewMode] = useState<"info" | "edit">(
    isEdit ? "info" : "edit",
  );

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const hydrateGroupForm = (group: Group) => {
    const orgAssociation = group.admin?.associations?.find(
      (aso) => aso.organizationId === currOrganization?.id,
    );
    setValues({
      name:
        orgAssociation?.personalDetails?.name || group.admin?.name || "",
      id:
        orgAssociation?.personalDetails?.numberID ||
        group.admin?.numberID ||
        "",
      phoneNumber: group.admin?.phoneNumber?.replace("+972", "0") ?? "",
      email:
        orgAssociation?.personalDetails?.email || group.admin?.email || "",
      groupName: group.name || "",
      size: group.size || "",
      description: group.description || "",
      comments: group.comments || "",
      groupCoordinator: group.groupCoordinator || "",
      transportMode: group.transportMode || "",
      type: group.type || "",
      status: group.status?.id || "",
      team: group.team || undefined,
      preferredVolunteerRegions: parseGroupVolunteerRegions(
        group.preferredVolunteerRegions,
      ),
    });
  };

  useEffect(() => {
    if (isEdit && groupDetails) {
      hydrateGroupForm(groupDetails);
      setDetailsViewMode("info");
    } else if (!isEdit) {
      setDetailsViewMode("edit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate when dialog identity changes
  }, [isEdit, groupDetails?.id, isOpen]);

  useAsyncEffect(async () => {
    if (!statusOptions.length) {
      const status = await getStatusOptions(
        "GROUP",
        currOrganization?.id ?? "",
      );
      setStatusOptions(status);
    }
  }, [currOrganization]);

  const buildLeaderPayload = (formValues: typeof values) => ({
    name: formValues.name,
    phoneNumber:
      isEdit && groupDetails?.admin?.phoneNumber
        ? groupDetails.admin.phoneNumber.replace("+972", "0")
        : formValues.phoneNumber,
    numberID: formValues.id,
    email: formValues.email || undefined,
  });

  const buildGroupPayload = (formValues: typeof values) => ({
    name: formValues.groupName,
    description: formValues.description || "",
    type: formValues.type || "",
    size: Number(formValues.size),
    comments: formValues.comments || "",
    groupCoordinator: formValues.groupCoordinator || "",
    transportMode: formValues.transportMode || "",
    teamId: formValues.team?.id,
    preferredVolunteerRegions: formValues.preferredVolunteerRegions,
  });

  const saveGroup = async () => {
    if (isLoading) return;

    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setTouched(
        Object.fromEntries(
          Object.keys(validationErrors).map((field) => [field, true]),
        ),
        true,
      );
      setAlertText("יש לתקן את השדות המסומנים לפני השמירה");
      setSeverity("error");
      setTime(3000);
      return;
    }

    try {
      setIsLoading(true);
      if (isEdit) {
        await linko.group.mutations.updateGroupFromAdmin({
          groupId: groupDetails?.id || "",
          leader: buildLeaderPayload(values),
          group: buildGroupPayload(values),
          statusId: values.status || undefined,
        });
        setAlertText("הקבוצה נערכה בהצלחה");
        setSeverity("success");
        resetForm();
        onEditComplete?.();
        onClose();
        return;
      }

      await linko.group.mutations.createGroupFromAdmin({
        leader: buildLeaderPayload(values),
        group: buildGroupPayload(values),
        statusId: values.status || undefined,
      });

      setAlertText("הקבוצה נוצרה בהצלחה");
      setSeverity("success");
      resetForm();
      onSaveComplete?.();
      onClose();
    } catch (error) {
      setAlertText(
        getRpcErrorMessage(
          error,
          "קרתה שגיאה בשמירת הקבוצה, אנא נסה שוב מאוחר יותר",
        ),
      );
      setSeverity("error");
      setTime(3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        disablePortal
        hideBackdrop
        PaperProps={{
          sx: {
            bgcolor: "white",
            borderRadius: "35px",
            width: "50vw",
            height: "80vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          },
        }}
        open={isOpen}
        onClose={() => {
          onClose();
          resetForm();
        }}
        className="admin-add-group-dialog"
      >
        <Box sx={{
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          <SelectButtonsContainer>
            <SelectButton
              onClick={() => handleTabChange(0)}
              content="פרטים"
              isChosen={tabValue === 0}
            />
            {currOrganization?.organizationType === "PREMIUM" && <SelectButton
              onClick={() => handleTabChange(1)}
              content="תקשורת"
              isChosen={tabValue === 1}
            />}
            {currOrganization?.organizationType === "PREMIUM" && <SelectButton
              onClick={() => handleTabChange(2)}
              content="מטלות"
              isChosen={tabValue === 2}
            />}

          </SelectButtonsContainer>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '1rem 2rem' }}>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {isEdit && detailsViewMode === "info" ? (
                <Box sx={{ overflow: "auto", flex: 1, pr: 0.5 }}>
                  <GroupInfoView
                    values={values}
                    statusTitle={
                      statusOptions.find((s) => s.id === values.status)?.title ||
                      groupDetails?.status?.title ||
                      "-"
                    }
                    onEdit={() => setDetailsViewMode("edit")}
                    onViewMembers={
                      groupDetails?.id
                        ? () => navigate(`/volunteers/groups/${groupDetails.id}`)
                        : undefined
                    }
                  />
                </Box>
              ) : (
                <Stack className="fields-container" sx={{ p: 3, overflow: 'auto', flex: 1 }}>
                  {isEdit && (
                    <Button
                      sx={{
                        borderRadius: "1rem",
                        display: "flex",
                        gap: "0.5rem",
                        border: "1px solid navy",
                        marginBottom: "1rem",
                        color: "navy",
                        width: "fit-content",
                      }}
                      color="secondary"
                      onClick={() => {
                        navigate(`/volunteers/groups/${groupDetails?.id}`);
                      }}
                    >
                      <RemoveRedEyeIcon />
                      צפייה במתנדבי הקבוצה
                    </Button>
                  )}
                  <FormTextField name="groupName" label="שם הקבוצה" />
                  <FormTextField name="size" label="גודל הקבוצה" />
                  <FormTextField
                    name="description"
                    className="description"
                    minRows={4}
                    multiline
                    label="תיאור הקבוצה"
                  />
                  <FormSelect
                    name="type"
                    label="סוג קבוצה"
                    options={[
                      "תנועת נוער",
                      "התארגנות פרטית",
                      "מכינה או שנת שירות",
                      "כוחות הביטחון",
                      "חברה עסקית",
                      "בית ספר",
                      "גוף ממשלתי",
                      "קרן/פדרציה",
                      'קבוצות מחו"ל',
                      "אחר",
                    ]}
                  />
                  <FormSelect
                    name="status"
                    label="סטטוס"
                    options={statusOptions.map((stat) => ({
                      content: stat.title,
                      value: stat.id
                    }))}
                  />
                  <Box sx={{
                    margin: '1rem 0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '16px',
                    padding: '8px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {values.team?.name || "לא נבחרה קבוצה"}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setIsTeamsSelectOpen(true)}
                      sx={{ textTransform: 'none', fontWeight: 'bold', padding: '0.5rem 1rem', borderRadius: "0.5rem" }}
                    >
                      הוספת קבוצה
                    </Button>
                  </Box>
                  <FormTextField name="name" label="שם המוביל" />
                  <FormTextField
                    name="id"
                    limit={9}
                    label="ת.ז"
                    withInfo="יש להזין ת.ז לטובת ביטוח מתנדבים"
                  />
                  <FormTextField
                    name="phoneNumber"
                    limit={10}
                    label="מספר טלפון"
                    disabled={isEdit}
                  />
                  <FormTextField name="email" label="מייל" />
                  <FormTextField name="groupCoordinator" label="מתאם הקבוצה" />
                  <FormTextField name="transportMode" label="תיאומים נדרשים" />
                  <FormTextField name="comments" label="הערות" />
                  <GroupVolunteerRegionsField
                    value={parseGroupVolunteerRegions(
                      values.preferredVolunteerRegions,
                    )}
                    onChange={(regions: GroupVolunteerRegion[]) =>
                      setFieldValue("preferredVolunteerRegions", regions, true)
                    }
                    error={
                      typeof errors.preferredVolunteerRegions === "string"
                        ? errors.preferredVolunteerRegions
                        : undefined
                    }
                  />
                </Stack>
              )}
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            {groupDetails && (
              <CommunicationRecordManager groupId={groupDetails.id} />
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <MissionList
              filter={groupDetails?.id ? { groupId: groupDetails?.id } : undefined}
              mission={{
                groupId: groupDetails?.id || "",
                group: groupDetails
              }}
              missionType="GROUP"
            />
          </TabPanel>
        </Box>

        {tabValue === 0 && (!isEdit || detailsViewMode === "edit") && (
          <Box sx={{ p: 1, borderTop: '1px solid rgba(0, 0, 0, 0.12)', display: "flex", justifyContent: "flex-end", gap: 1, padding: "0.6rem" }}>
            {isEdit && (
              <Button
                variant="outlined"
                onClick={() => {
                  if (groupDetails) hydrateGroupForm(groupDetails);
                  setDetailsViewMode("info");
                }}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                ביטול
              </Button>
            )}
            <BasicButton
              style={{
                width: "fit-content",
                paddingInline: "1rem",
                margin: 0,
                paddingBlock: "0.3rem",
                borderRadius: "0.5rem",
                marginInlineEnd: "0.5rem",
              }}
              text={isEdit ? "שמירה" : "הוספה"}
              isProgress={isLoading}
              disabled={!isValid || isLoading}
              onClick={saveGroup}
            />
          </Box>
        )}
        <GeneralAlert />
      </Dialog>
      <TeamsSelectDialog
        onClose={() => setIsTeamsSelectOpen(false)}
        open={isTeamsSelectOpen}
        isOne
        returnFullTeam
        onSave={(team) => {
          setFieldValue("team", team?.[1] || team?.[0] || undefined);
          setIsTeamsSelectOpen(false);
        }}
        value={values.team ? [values.team] : []}
      />
    </ThemeProvider>
  );
};

function GroupInfoView({
  values,
  statusTitle,
  onEdit,
  onViewMembers,
}: {
  values: Record<string, any>;
  statusTitle: string;
  onEdit: () => void;
  onViewMembers?: () => void;
}) {
  const regions = parseGroupVolunteerRegions(values.preferredVolunteerRegions);
  const phone = String(values.phoneNumber || "");
  const phoneForCell = phone.startsWith("0")
    ? `+972${phone.slice(1)}`
    : phone;

  return (
    <CardInfoView>
      <CardInfoSection>
        <CardInfoHeader
          title={values.groupName || "-"}
          onEdit={onEdit}
          subtitle={
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {values.type || "ללא סוג"}
              </Typography>
              {onViewMembers ? (
                <Button
                  size="medium"
                  startIcon={<RemoveRedEyeIcon />}
                  onClick={onViewMembers}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  מתנדבי הקבוצה
                </Button>
              ) : null}
            </Stack>
          }
          extra={
            <Box sx={{ mt: 1.25 }}>
              <CardInfoChips
                items={[
                  `סטטוס: ${statusTitle}`,
                  values.size ? `${values.size} משתתפים` : "",
                  values.team?.name ? `צוות: ${values.team.name}` : "",
                ].filter(Boolean)}
              />
            </Box>
          }
        />
      </CardInfoSection>

      <CardInfoSection title="פרטי הקבוצה">
        <CardInfoGrid>
          <CardInfoField label="תיאור" fullWidth>
            {values.description || "-"}
          </CardInfoField>
          <CardInfoField label="מתאם הקבוצה">
            {values.groupCoordinator || "-"}
          </CardInfoField>
          <CardInfoField label="תיאומים נדרשים">
            {values.transportMode || "-"}
          </CardInfoField>
          <CardInfoField label="אזורי התנדבות מועדפים" fullWidth>
            <CardInfoChips items={regions} />
          </CardInfoField>
          <CardInfoField label="הערות" fullWidth>
            {values.comments || "-"}
          </CardInfoField>
        </CardInfoGrid>
      </CardInfoSection>

      <CardInfoSection title="פרטי המוביל">
        <CardInfoGrid>
          <CardInfoField label="שם">{values.name || "-"}</CardInfoField>
          <CardInfoField label="ת.ז">{values.id || "-"}</CardInfoField>
          <CardInfoField label="טלפון">
            {phoneForCell ? (
              <PhoneNumberCell phoneNumber={phoneForCell} />
            ) : (
              "-"
            )}
          </CardInfoField>
          <CardInfoField label="מייל">{values.email || "-"}</CardInfoField>
        </CardInfoGrid>
      </CardInfoSection>
    </CardInfoView>
  );
}

export default provide([
  FormProvider,
  {
    initialValues: {
      phoneNumber: "",
      id: "",
      groupName: "",
      description: "",
      type: "",
      status: null,
      name: "",
      size: "",
      email: "",
      comments: "",
      groupCoordinator: "",
      transportMode: "",
      team: undefined,
      preferredVolunteerRegions: [] as GroupVolunteerRegion[],
    },
    className: "group-form",
    validationSchema,
    onSubmit: () => { },
  },
])(GroupCard);
